// Ganesh Modak Booking & Payment Management (iter172)
// A sub-module inside Booking Intelligence — completely isolated
// from the existing table/tiffin/catering/event booking flows.
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Plus, IndianRupee, Copy, Loader2, RefreshCw, Download, AlertTriangle,
  Send, CheckCircle2, Package, FileSpreadsheet, Trash2, Printer,
  Filter, FileText, ArrowLeft,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL;
const GM = `${API}/api/ganesh-modak`;

const STATUS_COLOR = {
  PAYMENT_PENDING: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  PAYMENT_OVERDUE: "bg-red-500/20 text-red-700 border-red-500/40",
  CONFIRMED: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  LATE_PAYMENT_CONFIRMED: "bg-emerald-600/15 text-emerald-800 border-emerald-600/30",
  IN_PRODUCTION: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  READY: "bg-indigo-500/15 text-indigo-700 border-indigo-500/30",
  PICKED_UP: "bg-slate-500/15 text-slate-700 border-slate-500/30",
  DELIVERED: "bg-slate-500/15 text-slate-700 border-slate-500/30",
  CANCELLED: "bg-neutral-400/20 text-neutral-700 border-neutral-400/40",
  VOIDED: "bg-fuchsia-500/15 text-fuchsia-800 border-fuchsia-500/40",
};

const fmtInr = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const copyToClipboard = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Copy failed — select the text manually"),
  );
};

// ────────────────────────────────────────────────────────────────────
// Main component with sub-tabs
// ────────────────────────────────────────────────────────────────────
export default function GaneshModakOrders() {
  const { session } = useAuth();
  const token = session?.token;
  const isSA = !!session?.is_super_admin;
  const isAdmin = !!session?.is_admin;
  const [tab, setTab] = useState("dashboard");
  const [campaign, setCampaign] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const bump = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!token) return;
    axios.post(`${GM}/campaign/get`, { token }).then((r) => setCampaign(r.data));
  }, [token]);

  if (!token) return null;
  if (!campaign) {
    return (
      <div className="p-8 flex items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading Ganesh Modak module…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gm-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#8B0000] flex items-center gap-2">
            ॥ ॐ गं गणपतये नमः ॥ Ganesh Modak Orders
          </h2>
          <p className="text-xs text-muted-foreground">
            Campaign {campaign.campaign_year} • Window {campaign.booking_start_date} → {campaign.booking_end_date}
            {" • "}
            Payment lead: {campaign.payment_lead_days} days
            {" • "}Bulk discount: {campaign.bulk_discount_percent}% above{" "}
            {campaign.bulk_discount_threshold_pieces} pcs
            {" • "}
            <span className={campaign.active ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
              {campaign.active ? "ACTIVE" : "CLOSED"}
            </span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <NewOrderButton token={token} campaign={campaign} session={session} onCreated={bump} />
          {isSA && (
            <Button variant="outline" data-testid="gm-scan-reminders"
              onClick={async () => {
                try {
                  const r = await axios.post(`${GM}/reminders/scan`, { token });
                  toast.success(
                    `Scan done — ${r.data.reminders_created} new reminders, ${r.data.overdue_flipped} flipped to OVERDUE`,
                  );
                  bump();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Scan failed");
                }
              }}>
              <RefreshCw className="w-4 h-4 mr-1" /> Run Reminder Scan
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="dashboard" data-testid="gm-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="orders" data-testid="gm-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="gm-tab-reminders">Reminders</TabsTrigger>
          <TabsTrigger value="production" data-testid="gm-tab-production">Production Plan</TabsTrigger>
          <TabsTrigger value="reports" data-testid="gm-tab-reports">Reports</TabsTrigger>
          {isSA && (<TabsTrigger value="settings" data-testid="gm-tab-settings">Campaign Settings</TabsTrigger>)}
        </TabsList>

        <TabsContent value="dashboard" className="space-y-3">
          <DashboardTab token={token} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="orders" className="space-y-3">
          <OrdersTab token={token} campaign={campaign} isSA={isSA} isAdmin={isAdmin}
                       session={session} refreshKey={refreshKey} bump={bump} />
        </TabsContent>
        <TabsContent value="reminders" className="space-y-3">
          <RemindersTab token={token} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="production" className="space-y-3">
          <ProductionTab token={token} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="reports" className="space-y-3">
          <ReportsTab token={token} isSA={isSA} />
        </TabsContent>
        {isSA && (
          <TabsContent value="settings" className="space-y-3">
            <CampaignSettingsTab token={token} campaign={campaign}
                                    onSaved={(c) => { setCampaign(c); bump(); }} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// New Order button + dialog
// ────────────────────────────────────────────────────────────────────
function NewOrderButton({ token, campaign, session, onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button className="bg-[#8B0000] hover:bg-[#6b0000]" data-testid="gm-new-order-btn"
        onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1" /> New Modak Order
      </Button>
      {open && (
        <NewOrderDialog token={token} campaign={campaign} session={session}
          onClose={() => setOpen(false)}
          onCreated={() => { setOpen(false); onCreated(); }} />
      )}
    </>
  );
}

function NewOrderDialog({ token, campaign, session, onClose, onCreated }) {
  const [centers, setCenters] = useState([]);
  // iter267 — the campaign received here is the GLOBAL default. When the
  // dialog user selects PB-PERTH (or any centre with pricing override),
  // we re-fetch the centre-scoped campaign so packages / currency / GST%
  // match. `activeCampaign` is what we render + use for compute.
  const [centerCampaign, setCenterCampaign] = useState(campaign);
  const activeCampaign = centerCampaign || campaign;
  const currencySymbol = activeCampaign.currency_symbol || "₹";
  const fmtMoney = (v) =>
    `${currencySymbol}${Number(v || 0).toLocaleString(
      currencySymbol === "₹" ? "en-IN" : "en-AU",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    )}`;
  const [form, setForm] = useState({
    center: session?.center || "",
    customer_name: "",
    phone: "",
    whatsapp: "",
    pickup_date: campaign.default_pickup_dates?.[0] || "",
    pickup_time: "",
    packages: campaign.packages.map((p) => ({ code: p.code, quantity: 0 })),
    manual_discount: 0,
    notes: "",
    booking_source: "Phone Call",
  });
  const [totals, setTotals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null);
  const [overrideDupe, setOverrideDupe] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/centers`).then((r) => {
      const list = (r.data.centers || r.data || []);
      const filtered = session?.is_super_admin ? list
        : session?.is_admin
          ? list.filter((c) => (session.assigned_centers || []).includes(c.code))
          : list.filter((c) => c.code === session?.center);
      setCenters(filtered);
    });
  }, [session]);

  // iter267 — When centre changes, refetch centre-scoped campaign so
  // Perth shows AUD prices + 10% GST + only Steam packs.
  useEffect(() => {
    if (!token || !form.center) return;
    let cancelled = false;
    axios.post(`${GM}/campaign/get`, { token, center: form.center })
      .then((r) => {
        if (cancelled) return;
        setCenterCampaign(r.data);
        // Rebuild the qty rows for whatever packages the centre offers.
        setForm((f) => ({
          ...f,
          packages: (r.data.packages || []).map((p) => {
            const prev = f.packages.find((x) => x.code === p.code);
            return { code: p.code, quantity: prev?.quantity || 0 };
          }),
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, form.center]);

  // Live totals
  useEffect(() => {
    const nonZero = form.packages.filter((p) => p.quantity > 0);
    if (nonZero.length === 0) { setTotals(null); return; }
    let cancelled = false;
    axios.post(`${GM}/compute`, {
      token, packages: nonZero,
      manual_discount: Number(form.manual_discount || 0),
      pickup_date: form.pickup_date || null,
      center: form.center,   // iter267 — centre-scoped totals
    }).then((r) => { if (!cancelled) setTotals(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, form.packages, form.manual_discount, form.pickup_date, form.center]);

  const setQty = (code, qty) => setForm((f) => ({
    ...f,
    packages: f.packages.map((p) => p.code === code ? { ...p, quantity: Math.max(0, Number(qty) || 0) } : p),
  }));

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    if (!form.center) return toast.error("Centre is required");
    if (!form.pickup_date) return toast.error("Pickup date is required");
    const pkgs = form.packages.filter((p) => p.quantity > 0);
    if (pkgs.length === 0) return toast.error("Pick at least one package");
    setBusy(true);
    try {
      const r = await axios.post(`${GM}/orders/create`, {
        token, center: form.center,
        customer_name: form.customer_name, phone: form.phone,
        whatsapp: form.whatsapp || form.phone,
        pickup_date: form.pickup_date, pickup_time: form.pickup_time,
        packages: pkgs, manual_discount: Number(form.manual_discount || 0),
        notes: form.notes, booking_source: form.booking_source,
        override_duplicate: overrideDupe,
      });
      if (r.data.possible_duplicates) {
        setDupes(r.data.possible_duplicates);
        toast.warning("Possible existing Modak booking found. Review below.");
      } else {
        toast.success(`Order created — ${r.data.order.order_number} • Deadline ${r.data.order.payment_deadline}`);
        setLastCreated(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="gm-new-order-dialog">
        <DialogHeader>
          <DialogTitle>New Ganesh Modak Order</DialogTitle>
          <DialogDescription>
            Create a new Ganesh Festival Modak booking. It will be saved as PAYMENT PENDING until payment is recorded.
          </DialogDescription>
        </DialogHeader>

        {lastCreated ? (
          <WhatsAppPreview order={lastCreated.order} whatsapp={lastCreated.whatsapp}
                              onClose={() => { setLastCreated(null); onCreated(); }} />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label>Centre *</Label>
                <Select value={form.center}
                  onValueChange={(v) => setForm((f) => ({ ...f, center: v }))}>
                  <SelectTrigger data-testid="gm-form-center"><SelectValue placeholder="Pick centre" /></SelectTrigger>
                  <SelectContent>
                    {centers.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer Name *</Label>
                <Input value={form.customer_name} data-testid="gm-form-name"
                  onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} data-testid="gm-form-phone"
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <Label>WhatsApp (if different)</Label>
                <Input value={form.whatsapp} data-testid="gm-form-whatsapp"
                  onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} />
              </div>
              <div>
                <Label>Pickup Date *</Label>
                <Input type="date" value={form.pickup_date} data-testid="gm-form-pickup-date"
                  onChange={(e) => setForm((f) => ({ ...f, pickup_date: e.target.value }))} />
              </div>
              <div>
                <Label>Pickup Time</Label>
                <Input placeholder="e.g. 10:00 AM" value={form.pickup_time} data-testid="gm-form-pickup-time"
                  onChange={(e) => setForm((f) => ({ ...f, pickup_time: e.target.value }))} />
              </div>
            </div>

            <div className="border rounded p-3 bg-amber-50/30">
              <div className="text-sm font-semibold mb-2 flex items-center justify-between">
                <span>Modak Packages</span>
                <span className="text-xs font-normal text-muted-foreground">
                  Currency: <strong>{activeCampaign.currency || "INR"}</strong>
                  {" · GST "}
                  <strong>{activeCampaign.gst_percent ?? 5}%</strong>
                </span>
              </div>
              {(() => {
                const steamPkgs = (activeCampaign.packages || []).filter((p) => (p.type || "STEAM").toUpperCase() === "STEAM");
                const friedPkgs = (activeCampaign.packages || []).filter((p) => (p.type || "STEAM").toUpperCase() === "FRIED");
                const renderGroup = (title, badgeCls, pkgs) => (
                  <div className="mb-3">
                    <div className={`inline-block text-[11px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${badgeCls} mb-2`}>
                      {title}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {pkgs.map((p) => (
                        <div key={p.code} className="border rounded p-2 bg-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{p.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {fmtMoney(p.price)} + GST / box
                              </div>
                            </div>
                            <Input type="number" min={0} className="w-20" data-testid={`gm-form-qty-${p.code}`}
                              value={form.packages.find((x) => x.code === p.code)?.quantity || 0}
                              onChange={(e) => setQty(p.code, e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
                return (
                  <>
                    {steamPkgs.length > 0 && renderGroup("Steam Modak", "bg-emerald-100 text-emerald-800 border border-emerald-300", steamPkgs)}
                    {friedPkgs.length > 0 && renderGroup("Fried Modak", "bg-amber-100 text-amber-800 border border-amber-300", friedPkgs)}
                  </>
                );
              })()}
              <div className="mt-2 grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
                <StatCard label="Total Pieces" value={totals?.totals?.total_pieces || 0}
                  hint={
                    ((totals?.totals?.steam_pieces || 0) + (totals?.totals?.fried_pieces || 0)) > 0
                      ? `Steam ${totals?.totals?.steam_pieces || 0} · Fried ${totals?.totals?.fried_pieces || 0}`
                      : null
                  } />
                <StatCard label="Subtotal" value={fmtMoney(totals?.totals?.gross_amount || 0)} />
                <StatCard label="Bulk Discount"
                  value={fmtMoney(totals?.totals?.bulk_discount || 0)}
                  hint={totals?.totals?.bulk_eligible ? `${activeCampaign.bulk_discount_percent}% applied` : (activeCampaign.bulk_discount_threshold_pieces > 1000 ? "N/A" : `> ${activeCampaign.bulk_discount_threshold_pieces} pcs needed`)} />
                <StatCard label="Base (pre-GST)" value={fmtMoney(totals?.totals?.taxable_amount || 0)} />
                <StatCard label={`GST @ ${totals?.totals?.gst_percent ?? activeCampaign.gst_percent ?? 5}%`}
                  value={fmtMoney(totals?.totals?.gst_amount || 0)} />
                <StatCard label="TOTAL PAYABLE (incl. GST)"
                  value={fmtMoney(totals?.totals?.final_payable || totals?.totals?.final_amount || 0)} strong />
              </div>
              {totals?.payment_deadline && (
                <div className="mt-2 text-xs">
                  Payment Deadline (auto): <strong className="text-red-700">{totals.payment_deadline}</strong> (Pickup − {activeCampaign.payment_lead_days} days)
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Manual Discount ({currencySymbol})</Label>
                <Input type="number" value={form.manual_discount} data-testid="gm-form-manual-discount"
                  onChange={(e) => setForm((f) => ({ ...f, manual_discount: e.target.value }))} />
              </div>
              <div>
                <Label>Booking Source</Label>
                <Select value={form.booking_source}
                  onValueChange={(v) => setForm((f) => ({ ...f, booking_source: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Phone Call", "Walk-in", "WhatsApp", "Instagram", "Reference", "Website", "Other"]
                      .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} data-testid="gm-form-notes"
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>

            {dupes && dupes.length > 0 && (
              <Alert variant="destructive" data-testid="gm-dup-warning">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>Possible existing Modak booking</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc ml-4 mt-1 text-xs">
                    {dupes.map((d, i) => (
                      <li key={i}>
                        <strong>{d.order_number}</strong> — {d.status} — {d.totals?.total_pieces} pcs
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={overrideDupe}
                      onChange={(e) => setOverrideDupe(e.target.checked)} data-testid="gm-override-dupe" />
                    <span>Yes, create anyway (legitimate second booking)</span>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Alert className="border-amber-400 bg-amber-50 text-amber-900">
              <AlertTitle>Payment Rule</AlertTitle>
              <AlertDescription className="text-xs">
                Order will be saved as <strong>PAYMENT PENDING</strong>. It will only become
                CONFIRMED after the payment is recorded. Bookings paid after the deadline
                are marked <strong>LATE PAYMENT CONFIRMATION</strong> with full audit.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="gm-form-submit">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Save Order (Payment Pending)
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, hint, strong }) {
  return (
    <div className={`p-2 rounded border ${strong ? "bg-emerald-50 border-emerald-300" : "bg-white"}`}>
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className={`font-semibold ${strong ? "text-lg text-emerald-800" : ""}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Dashboard tab
// ────────────────────────────────────────────────────────────────────
function DashboardTab({ token, refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.post(`${GM}/dashboard/summary`, { token });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    }
  }, [token]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return <Loader2 className="w-4 h-4 animate-spin" />;

  return (
    <div className="space-y-4" data-testid="gm-dashboard">
      <Section title="Today's Bookings">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Metric label="Orders Created" value={data.today_bookings.count} />
          <Metric label="Modaks Booked" value={data.today_bookings.pieces} />
          <Metric label="Value" value={fmtInr(data.today_bookings.amount)} />
          <Metric label="Payment Due Today" value={data.payments.due_today.count}
            strong={data.payments.due_today.count > 0} />
        </div>
      </Section>

      <Section title="Festival Totals">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Metric label="Total Orders" value={data.festival.total_orders} />
          <Metric label="Total Modaks" value={data.festival.total_modaks} />
          <Metric label="Confirmed Modaks" value={data.festival.confirmed_modaks} strong />
          <Metric label="Pending Modaks" value={data.festival.pending_modaks} />
          <Metric label="Total Collection" value={fmtInr(data.festival.collection)} />
          <Metric label="Outstanding Amount" value={fmtInr(data.festival.pending_amount)} />
        </div>
      </Section>

      <Section title="Payments">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Metric label="Payment Pending" value={data.payments.payment_pending.count} />
          <Metric label="Overdue"
            value={data.payments.payment_overdue.count}
            strong={data.payments.payment_overdue.count > 0} />
          <Metric label="Due Today" value={data.payments.due_today.count} />
          <Metric label="Due Tomorrow" value={data.payments.due_tomorrow.count} />
        </div>
      </Section>

      <Section title="Fulfilment">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Metric label="Today's Pickups" value={data.fulfilment.today_pickups.count} />
          <Metric label="In Production" value={data.fulfilment.in_production.count} />
          <Metric label="Ready" value={data.fulfilment.ready.count} />
          <Metric label="Picked Up" value={data.fulfilment.picked_up.count} />
          <Metric label="Delivered" value={data.fulfilment.delivered.count} />
          <Metric label="Pending Fulfilment" value={data.fulfilment.pending_fulfilment.count} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-sm font-semibold mb-2 text-[#8B0000]">{title}</div>
        {children}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, strong }) {
  return (
    <div className={`p-3 rounded border ${strong ? "bg-red-50 border-red-300" : "bg-white"}`}>
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold ${strong ? "text-red-700" : ""}`}>{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Orders list tab
// ────────────────────────────────────────────────────────────────────
function OrdersTab({ token, campaign, refreshKey, bump, isSA, isAdmin }) {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [center, setCenter] = useState("all");
  const [centers, setCenters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [loading, setLoading] = useState(false);
  const canDelete = !!(isSA || isAdmin);

  useEffect(() => {
    axios.get(`${API}/api/centers`).then((r) => setCenters(r.data.centers || r.data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${GM}/orders/list`, {
        token,
        status: status === "all" ? null : status,
        center: center === "all" ? null : center,
        query: query || null,
        include_voided: isSA ? includeVoided : false,
        limit: 500,
      });
      setOrders(r.data.orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [token, status, center, query, includeVoided, isSA]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="space-y-3" data-testid="gm-orders-tab">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52" data-testid="gm-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="PENDING_OR_OVERDUE">Payment Pending / Overdue</SelectItem>
              <SelectItem value="PAYMENT_PENDING">Payment Pending only</SelectItem>
              <SelectItem value="PAYMENT_OVERDUE">Payment Overdue</SelectItem>
              <SelectItem value="CONFIRMED">Confirmed</SelectItem>
              <SelectItem value="LATE_PAYMENT_CONFIRMED">Late-Confirmed</SelectItem>
              <SelectItem value="IN_PRODUCTION">In Production</SelectItem>
              <SelectItem value="READY">Ready</SelectItem>
              <SelectItem value="PICKED_UP">Picked Up</SelectItem>
              <SelectItem value="DELIVERED">Delivered</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
              {isSA && (<SelectItem value="VOIDED">Voided (audit)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Centre</Label>
          <Select value={center} onValueChange={setCenter}>
            <SelectTrigger className="w-40" data-testid="gm-filter-center"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Centres</SelectItem>
              {centers.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search (name / phone / order#)</Label>
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()}
                   data-testid="gm-filter-query" />
        </div>
        <Button variant="outline" onClick={load} data-testid="gm-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        {isSA && (
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none ml-1"
                    data-testid="gm-toggle-voided">
            <Checkbox checked={includeVoided}
                        onCheckedChange={(v) => setIncludeVoided(!!v)} />
            Include Voided / Deleted (audit)
          </label>
        )}
      </div>

      <div className="border rounded overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order No</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Centre</TableHead>
              <TableHead>Pickup</TableHead>
              <TableHead>Pieces</TableHead>
              <TableHead>Final ₹</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && orders.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground">No orders match</TableCell></TableRow>
            )}
            {orders.map((o) => {
              const overdue = o.status === "PAYMENT_OVERDUE";
              return (
                <TableRow key={o.order_number}
                  className={overdue ? "bg-red-50/60" : ""}
                  data-testid={`gm-order-row-${o.order_number}`}>
                  <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                  <TableCell>
                    <div className="font-medium">{o.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{o.phone}</div>
                  </TableCell>
                  <TableCell>{o.center}</TableCell>
                  <TableCell>
                    <div>{o.pickup_date}</div>
                    <div className="text-xs text-muted-foreground">{o.pickup_time}</div>
                  </TableCell>
                  <TableCell className="font-semibold">{o.totals.total_pieces}</TableCell>
                  <TableCell>{fmtInr(o.totals.final_amount)}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLOR[o.status] || ""}>{o.status.replace(/_/g, " ")}</Badge>
                  </TableCell>
                  <TableCell className={overdue ? "text-red-700 font-semibold" : ""}>{o.payment_deadline}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setSelected(o)}
                        data-testid={`gm-open-${o.order_number}`}>Open</Button>
                      {canDelete && o.status !== "VOIDED" && (
                        <Button size="sm" variant="outline"
                          className="text-red-700 border-red-300 hover:bg-red-50"
                          onClick={() => setToDelete(o)}
                          data-testid={`gm-delete-${o.order_number}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {selected && (
        <OrderDetailDialog token={token} orderNumber={selected.order_number}
          campaign={campaign}
          onClose={() => setSelected(null)}
          onMutated={() => { setSelected(null); load(); bump && bump(); }} />
      )}

      {toDelete && (
        <DeleteOrderDialog token={token} order={toDelete}
          onClose={() => setToDelete(null)}
          onDone={() => { setToDelete(null); load(); bump && bump(); }} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Order detail dialog (payment + status + WhatsApp)
// ────────────────────────────────────────────────────────────────────
function OrderDetailDialog({ token, orderNumber, onClose, onMutated }) {
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.post(`${GM}/orders/get`, { token, order_number: orderNumber });
    setData(r.data);
  }, [token, orderNumber]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { order, audit, whatsapp } = data;
  const canRecordPayment = ["PAYMENT_PENDING", "PAYMENT_OVERDUE"].includes(order.status);
  const nextStatuses = STATUS_NEXT[order.status] || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="gm-order-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {order.order_number}
            <Badge className={STATUS_COLOR[order.status] || ""}>{order.status.replace(/_/g, " ")}</Badge>
          </DialogTitle>
          <DialogDescription>
            View order details, record payments, progress fulfilment status, and copy WhatsApp messages.
          </DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 text-sm">
            <div><strong>Customer:</strong> {order.customer_name}</div>
            <div><strong>Phone:</strong> {order.phone} {order.whatsapp && order.whatsapp !== order.phone && `(WA: ${order.whatsapp})`}</div>
            <div><strong>Centre:</strong> {order.center}</div>
            <div><strong>Pickup:</strong> {order.pickup_date} {order.pickup_time}</div>
            <div><strong>Total pieces:</strong> {order.totals.total_pieces}</div>
            <div><strong>Gross:</strong> {fmtInr(order.totals.gross_amount)}</div>
            <div><strong>Bulk Discount:</strong> {fmtInr(order.totals.bulk_discount)}
              {order.totals.bulk_eligible && <Badge className="ml-1 text-[10px]" variant="outline">10% applied</Badge>}
            </div>
            <div><strong>Taxable Amount:</strong> {fmtInr(order.totals.taxable_amount ?? (order.totals.gross_amount - order.totals.bulk_discount))}</div>
            <div><strong>GST @ {order.totals.gst_percent ?? 5}%:</strong> {fmtInr(order.totals.gst_amount ?? 0)}</div>
            <div className="text-base"><strong className="text-emerald-800">TOTAL PAYABLE (incl. GST):</strong>{" "}
              <strong className="text-emerald-800">{fmtInr(order.totals.final_payable ?? order.totals.final_amount)}</strong>
            </div>
            {order.payment_config?.upi_id && (
              <div className="text-xs mt-1 p-2 rounded bg-amber-50 border border-amber-300" data-testid="gm-order-upi-info">
                <strong>Payment UPI ({order.center}):</strong> {order.payment_config.upi_id}
                {" • "}<a className="underline text-blue-700" href={`${API}${order.payment_config.qr_url}`} target="_blank" rel="noreferrer">Open QR</a>
              </div>
            )}
            {/* iter264 — International / non-India centre payment note */}
            {order.payment_config?.mode === "DIRECT_CENTRE_PAYMENT" && (
              <div className="text-xs mt-1 p-2 rounded bg-sky-50 border border-sky-300"
                    data-testid="gm-order-direct-payment-info">
                <strong>Payment ({order.center}):</strong>{" "}
                {order.payment_config.instruction ||
                  `Payment will be collected/arranged directly by Purnabramha (${order.center}).`}
                <div className="mt-1 text-slate-600">
                  Accepted methods: Card at Centre / EFTPOS / Bank Transfer / Cash / Other.
                </div>
              </div>
            )}
            <div><strong>Payment Deadline:</strong>
              <span className={order.status === "PAYMENT_OVERDUE" ? "text-red-700 font-semibold ml-1" : "ml-1"}>
                {order.payment_deadline}
              </span>
            </div>
            {order.receipt_number && (
              <div><strong>Receipt:</strong> {order.receipt_number} • Paid {fmtInr(order.amount_paid)} on {order.payment_date} ({order.payment_mode})
                {order.upi_txn_ref && <span> • Ref: {order.upi_txn_ref}</span>}
              </div>
            )}
            {order.confirmed_by && (
              <div><strong>Confirmed by:</strong> {order.confirmed_by} at {order.confirmed_at?.slice(0, 19).replace("T", " ")}</div>
            )}
            {order.notes && <div><strong>Notes:</strong> <pre className="whitespace-pre-wrap text-xs bg-muted p-2 rounded">{order.notes}</pre></div>}
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">WhatsApp Message</div>
            <pre className="whitespace-pre-wrap bg-emerald-50 p-3 rounded border border-emerald-200 text-xs max-h-72 overflow-auto">{whatsapp.message}</pre>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(whatsapp.message)}
              data-testid="gm-copy-wa">
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>
        </div>

        <div className="border rounded p-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Packages</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Pieces</TableHead>
                <TableHead>Subtotal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.packages.map((ln, i) => {
                const t = (ln.type || "STEAM").toUpperCase();
                return (
                  <TableRow key={i}>
                    <TableCell>
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        t === "FRIED"
                          ? "bg-amber-100 text-amber-800 border border-amber-300"
                          : "bg-emerald-100 text-emerald-800 border border-emerald-300"}`}>
                        {t === "FRIED" ? "Fried" : "Steam"}
                      </span>
                    </TableCell>
                    <TableCell>{ln.label}</TableCell>
                    <TableCell>{ln.quantity}</TableCell>
                    <TableCell>{ln.pieces}</TableCell>
                    <TableCell>{fmtInr(ln.subtotal)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap gap-2">
          {canRecordPayment && (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPayOpen(true)}
              data-testid="gm-record-payment-btn">
              <IndianRupee className="w-4 h-4 mr-1" /> Record Payment
            </Button>
          )}
          {nextStatuses.map((s) => (
            <StatusButton key={s} token={token} order={order} newStatus={s} onDone={onMutated} />
          ))}
          {!["PICKED_UP", "DELIVERED", "CANCELLED"].includes(order.status) && (
            <StatusButton token={token} order={order} newStatus="CANCELLED"
              variant="destructive" onDone={onMutated} />
          )}
        </div>

        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Audit Trail</div>
          <div className="max-h-52 overflow-auto border rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>At</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{a.action}</TableCell>
                    <TableCell>{a.by}</TableCell>
                    <TableCell>{a.role}</TableCell>
                    <TableCell className="text-xs">{a.at?.slice(0, 19).replace("T", " ")}</TableCell>
                    <TableCell className="text-xs">
                      {a.details && Object.keys(a.details).length > 0
                        ? JSON.stringify(a.details) : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {payOpen && (
          <RecordPaymentDialog token={token} order={order}
            onClose={() => setPayOpen(false)}
            onDone={onMutated} />
        )}
      </DialogContent>
    </Dialog>
  );
}

const STATUS_NEXT = {
  CONFIRMED: ["IN_PRODUCTION", "READY"],
  LATE_PAYMENT_CONFIRMED: ["IN_PRODUCTION", "READY"],
  IN_PRODUCTION: ["READY"],
  READY: ["PICKED_UP", "DELIVERED"],
};

function StatusButton({ token, order, newStatus, variant, onDone }) {
  const [busy, setBusy] = useState(false);
  const label = newStatus.replace(/_/g, " ");
  const click = async () => {
    let reason = "";
    if (newStatus === "CANCELLED") {
      reason = window.prompt("Cancellation reason (required for paid orders):") || "";
      if (!reason && ["CONFIRMED", "LATE_PAYMENT_CONFIRMED", "IN_PRODUCTION", "READY"]
          .includes(order.status)) return;
    }
    setBusy(true);
    try {
      await axios.post(`${GM}/orders/status`, {
        token, order_number: order.order_number,
        new_status: newStatus, reason,
      });
      toast.success(`Order → ${label}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant={variant || "outline"} onClick={click} disabled={busy}
      data-testid={`gm-status-${newStatus}`}>
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> :
        newStatus === "CANCELLED" ? null : <CheckCircle2 className="w-4 h-4 mr-1" />}
      Mark {label}
    </Button>
  );
}

function RecordPaymentDialog({ token, order, onClose, onDone }) {
  const finalPay = order.totals.final_payable ?? order.totals.final_amount;
  const [amt, setAmt] = useState(finalPay);
  const [mode, setMode] = useState("UPI");
  const [dt, setDt] = useState(new Date().toISOString().slice(0, 10));
  const [receipt, setReceipt] = useState("");
  const [txnRef, setTxnRef] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState(null);

  const submit = async () => {
    if (!receipt.trim()) return toast.error("Receipt / Bill Number is required");
    setBusy(true);
    try {
      const r = await axios.post(`${GM}/orders/record-payment`, {
        token, order_number: order.order_number,
        amount_paid: Number(amt), payment_mode: mode,
        payment_date: dt, receipt_number: receipt,
        upi_txn_ref: txnRef, notes,
      });
      toast.success(`Payment recorded — order ${r.data.order.status}`);
      setWa(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gm-pay-dialog">
        <DialogHeader><DialogTitle>Record Payment — {order.order_number}</DialogTitle>
          <DialogDescription>Enter payment details to move this order from PAYMENT PENDING to CONFIRMED.</DialogDescription>
        </DialogHeader>
        {wa ? (
          <WhatsAppPreview order={wa.order} whatsapp={wa.whatsapp}
            onClose={() => { onClose(); onDone(); }} />
        ) : (
          <div className="space-y-3">
            <div className="text-xs bg-emerald-50 border border-emerald-200 p-2 rounded">
              <div>Order Value: <strong>{fmtInr(order.totals.taxable_amount ?? 0)}</strong></div>
              <div>GST @ {order.totals.gst_percent ?? 5}%: <strong>{fmtInr(order.totals.gst_amount ?? 0)}</strong></div>
              <div className="text-emerald-800 font-semibold">TOTAL PAYABLE (incl. GST): {fmtInr(finalPay)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount Received (incl. GST)</Label>
                <Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)}
                  data-testid="gm-pay-amount" />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="gm-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["UPI", "Cash", "Card", "Bank Transfer", "Cheque", "Other"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="gm-pay-date" />
              </div>
              <div>
                <Label>Receipt / Bill No *</Label>
                <Input value={receipt} onChange={(e) => setReceipt(e.target.value)}
                  data-testid="gm-pay-receipt" />
              </div>
              <div className="col-span-2">
                <Label>UPI Transaction Ref (if UPI)</Label>
                <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)}
                  placeholder="e.g. 4123XXXXXX" data-testid="gm-pay-txn-ref" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="gm-pay-notes" />
            </div>
            {order.status === "PAYMENT_OVERDUE" && (
              <Alert variant="destructive">
                <AlertTitle>Late Payment</AlertTitle>
                <AlertDescription className="text-xs">
                  This booking is past its {order.payment_deadline} deadline. It will be
                  marked as <strong>LATE PAYMENT CONFIRMED</strong> in the audit trail.
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="gm-pay-submit">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Confirm Payment
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppPreview({ order, whatsapp, onClose }) {
  return (
    <div className="space-y-3">
      <Alert className="border-emerald-400 bg-emerald-50">
        <AlertTitle>Order {order.order_number} — {order.status.replace(/_/g, " ")}</AlertTitle>
        <AlertDescription className="text-xs">
          Copy the WhatsApp message below and send to <strong>{order.whatsapp || order.phone}</strong>
        </AlertDescription>
      </Alert>
      <pre className="whitespace-pre-wrap bg-white p-3 rounded border text-xs max-h-72 overflow-auto"
        data-testid="gm-wa-message">{whatsapp.message}</pre>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => copyToClipboard(whatsapp.message)} data-testid="gm-wa-copy">
          <Copy className="w-4 h-4 mr-1" /> Copy Message
        </Button>
        <Button
          onClick={() => {
            const num = (order.whatsapp || order.phone || "").replace(/\D/g, "");
            const url = `https://wa.me/91${num}?text=${encodeURIComponent(whatsapp.message)}`;
            window.open(url, "_blank");
          }}
          className="bg-emerald-600 hover:bg-emerald-700" data-testid="gm-wa-open">
          <Send className="w-4 h-4 mr-1" /> Open in WhatsApp
        </Button>
        <div className="ml-auto">
          <Button variant="outline" onClick={onClose} data-testid="gm-wa-close">Done</Button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reminders tab
// ────────────────────────────────────────────────────────────────────
function RemindersTab({ token, refreshKey }) {
  const [items, setItems] = useState([]);
  const [onlyUnsent, setOnlyUnsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${GM}/reminders/list`, { token, only_unsent: onlyUnsent });
      setItems(r.data.reminders || []);
    } finally { setLoading(false); }
  }, [token, onlyUnsent]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const markSent = async (r) => {
    try {
      await axios.post(`${GM}/reminders/mark-sent`, { token,
        order_number: r.order_number, stage: r.stage });
      toast.success("Marked sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="gm-reminders-tab">
      <div className="flex items-center gap-3">
        <label className="text-xs flex items-center gap-2">
          <input type="checkbox" checked={onlyUnsent} onChange={(e) => setOnlyUnsent(e.target.checked)}
            data-testid="gm-reminders-only-unsent" />
          Only unsent
        </label>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>
      <div className="border rounded overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Stage</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Centre</TableHead>
              <TableHead>Planned</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={7} className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>)}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-4 text-muted-foreground">No reminders — run the scan from the top-right.</TableCell></TableRow>
            )}
            {items.map((r, i) => (
              <TableRow key={i} data-testid={`gm-reminder-row-${r.order_number}-${r.stage}`}>
                <TableCell className="font-mono text-xs">{r.stage}</TableCell>
                <TableCell className="font-mono text-xs">{r.order_number}</TableCell>
                <TableCell>{r.customer_name} <span className="text-xs text-muted-foreground">({r.phone})</span></TableCell>
                <TableCell>{r.center}</TableCell>
                <TableCell className="text-xs">{r.planned_at?.slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell className="text-xs">{r.sent_at ? r.sent_at.slice(0, 19).replace("T", " ") : <span className="text-amber-700">unsent</span>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(r.message)}
                      data-testid={`gm-reminder-copy-${r.order_number}-${r.stage}`}>
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => {
                        const num = (r.phone || "").replace(/\D/g, "");
                        window.open(`https://wa.me/91${num}?text=${encodeURIComponent(r.message)}`, "_blank");
                      }}>
                      <Send className="w-3 h-3" />
                    </Button>
                    {!r.sent_at && (
                      <Button size="sm" onClick={() => markSent(r)} className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`gm-reminder-mark-${r.order_number}-${r.stage}`}>
                        Mark Sent
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Production Plan tab
// ────────────────────────────────────────────────────────────────────
function ProductionTab({ token, refreshKey }) {
  const [data, setData] = useState({ confirmed: [], payment_pending: [] });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${GM}/production/requirement`, { token });
      setData(r.data);
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const confirmedTotal = useMemo(() =>
    data.confirmed.reduce((s, r) => s + r.total_pieces, 0), [data]);
  const pendingTotal = useMemo(() =>
    data.payment_pending.reduce((s, r) => s + r.total_pieces, 0), [data]);

  return (
    <div className="space-y-4" data-testid="gm-production-tab">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded border bg-emerald-50 border-emerald-300">
          <div className="text-xs uppercase text-emerald-800">Confirmed Production Quantity</div>
          <div className="text-3xl font-bold text-emerald-800">{confirmedTotal.toLocaleString()}</div>
          <div className="text-xs text-emerald-700">pieces (paid orders only)</div>
        </div>
        <div className="p-4 rounded border bg-amber-50 border-amber-300">
          <div className="text-xs uppercase text-amber-800">Potential — Payment Pending</div>
          <div className="text-3xl font-bold text-amber-800">{pendingTotal.toLocaleString()}</div>
          <div className="text-xs text-amber-700">pieces (NOT included in production)</div>
        </div>
      </div>

      <Section title="Confirmed by Centre / Pickup Slot">
        <ProductionTable rows={data.confirmed} />
      </Section>
      <Section title="Payment-Pending Potential">
        <ProductionTable rows={data.payment_pending} />
      </Section>

      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
    </div>
  );
}

function ProductionTable({ rows }) {
  if (!rows || rows.length === 0) return <div className="text-xs text-muted-foreground">No rows</div>;
  return (
    <div className="border rounded overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Centre</TableHead>
            <TableHead>Pickup Date</TableHead>
            <TableHead>Pickup Time</TableHead>
            <TableHead>4 pc</TableHead>
            <TableHead>11 pc</TableHead>
            <TableHead>21 pc</TableHead>
            <TableHead>Other Pieces</TableHead>
            <TableHead>Total Pieces</TableHead>
            <TableHead>Orders</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.center}</TableCell>
              <TableCell>{r.pickup_date}</TableCell>
              <TableCell>{r.pickup_time || "—"}</TableCell>
              <TableCell>{r.P4}</TableCell>
              <TableCell>{r.P11}</TableCell>
              <TableCell>{r.P21}</TableCell>
              <TableCell>{r.other_pieces}</TableCell>
              <TableCell className="font-semibold">{r.total_pieces}</TableCell>
              <TableCell>{r.order_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reports tab — preview-first (Orders + Production)
// ────────────────────────────────────────────────────────────────────
function ReportsTab({ token, isSA }) {
  const [mode, setMode] = useState(null); // null | "orders" | "production"
  if (mode === "orders") {
    return <OrdersReportPreview token={token} isSA={isSA} onBack={() => setMode(null)} />;
  }
  if (mode === "production") {
    return <ProductionReportPreview token={token} onBack={() => setMode(null)} />;
  }
  return (
    <div className="space-y-3" data-testid="gm-reports-tab">
      <div className="text-sm text-muted-foreground">
        Choose the report to preview. Downloads (Excel/PDF) are available inside the preview after applying filters.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Button variant="outline" className="h-auto py-6 justify-start"
          onClick={() => setMode("orders")}
          data-testid="gm-report-preview-orders">
          <FileSpreadsheet className="w-5 h-5 mr-3" />
          <div className="text-left">
            <div className="font-semibold text-base">Preview Orders Report</div>
            <div className="text-xs text-muted-foreground">
              Filter by centre, pickup, status, payment. Downloads reflect current filters.
            </div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-6 justify-start"
          onClick={() => setMode("production")}
          data-testid="gm-report-preview-production">
          <Package className="w-5 h-5 mr-3" />
          <div className="text-left">
            <div className="font-semibold text-base">Preview Production Requirement</div>
            <div className="text-xs text-muted-foreground">
              Confirmed + Paid orders only by default. Payment-pending shown separately.
            </div>
          </div>
        </Button>
      </div>
    </div>
  );
}

const TODAY = () => new Date().toISOString().slice(0, 10);

function SummaryCard({ label, value, tone }) {
  const cls = tone === "danger" ? "border-red-300 bg-red-50 text-red-800"
    : tone === "success" ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : tone === "warn" ? "border-amber-300 bg-amber-50 text-amber-800"
    : "bg-white";
  return (
    <div className={`p-3 rounded border ${cls}`}>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function downloadBlob(data, filename, mime) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function OrdersReportPreview({ token, isSA, onBack }) {
  const [centers, setCenters] = useState([]);
  const [filters, setFilters] = useState({
    center: "all",
    pickup_date: "",
    pickup_date_from: "",
    pickup_date_to: "",
    status: "all",
    payment_status: "all",
    query: "",
    bulk_only: false,
    include_voided: false,
  });
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/centers`).then((r) => setCenters(r.data.centers || r.data || []));
  }, []);

  const payload = useMemo(() => {
    const p = { token };
    if (filters.center && filters.center !== "all") p.center = filters.center;
    if (filters.pickup_date) p.pickup_date = filters.pickup_date;
    if (filters.pickup_date_from) p.pickup_date_from = filters.pickup_date_from;
    if (filters.pickup_date_to) p.pickup_date_to = filters.pickup_date_to;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    if (filters.payment_status && filters.payment_status !== "all") p.payment_status = filters.payment_status;
    if (filters.query) p.query = filters.query;
    if (filters.bulk_only) p.bulk_only = true;
    if (filters.include_voided) p.include_voided = true;
    p.limit = 5000;
    return p;
  }, [filters, token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${GM}/reports/orders-preview`, payload);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load preview");
    } finally { setLoading(false); }
  }, [payload]);

  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    setDownloading(true);
    try {
      const endpoint = fmt === "pdf" ? "export/orders-pdf" : "export/orders";
      const mime = fmt === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const r = await axios.post(`${GM}/${endpoint}`, payload, { responseType: "blob" });
      const ext = fmt === "pdf" ? "pdf" : "xlsx";
      downloadBlob(r.data, `modak_orders_${TODAY()}.${ext}`, mime);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally { setDownloading(false); }
  };

  const s = data.summary;
  return (
    <div className="space-y-3" data-testid="gm-orders-report-preview">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="gm-report-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reports
          </Button>
          <div className="font-semibold text-[#8B0000]">Preview — Orders Report</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="gm-print">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button size="sm" variant="outline" disabled={downloading}
            onClick={() => download("xlsx")}
            data-testid="gm-download-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Download Excel
          </Button>
          <Button size="sm" variant="outline" disabled={downloading}
            onClick={() => download("pdf")}
            data-testid="gm-download-pdf">
            <FileText className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border rounded p-3 bg-slate-50/60 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div>
            <Label className="text-xs">Centre</Label>
            <Select value={filters.center} onValueChange={(v) => setFilters(f => ({ ...f, center: v }))}>
              <SelectTrigger data-testid="gm-rp-center"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Centres</SelectItem>
                {centers.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Pickup Date (exact)</Label>
            <Input type="date" value={filters.pickup_date}
              onChange={e => setFilters(f => ({ ...f, pickup_date: e.target.value }))}
              data-testid="gm-rp-pickup" />
          </div>
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={filters.pickup_date_from}
              onChange={e => setFilters(f => ({ ...f, pickup_date_from: e.target.value }))}
              data-testid="gm-rp-from" />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={filters.pickup_date_to}
              onChange={e => setFilters(f => ({ ...f, pickup_date_to: e.target.value }))}
              data-testid="gm-rp-to" />
          </div>
          <div>
            <Label className="text-xs">Order Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="gm-rp-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PENDING_OR_OVERDUE">Pending / Overdue</SelectItem>
                <SelectItem value="PAYMENT_PENDING">Payment Pending</SelectItem>
                <SelectItem value="PAYMENT_OVERDUE">Payment Overdue</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="LATE_PAYMENT_CONFIRMED">Late-Confirmed</SelectItem>
                <SelectItem value="IN_PRODUCTION">In Production</SelectItem>
                <SelectItem value="READY">Ready</SelectItem>
                <SelectItem value="PICKED_UP">Picked Up</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                {isSA && (<SelectItem value="VOIDED">Voided (audit)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Payment Status</Label>
            <Select value={filters.payment_status} onValueChange={(v) => setFilters(f => ({ ...f, payment_status: v }))}>
              <SelectTrigger data-testid="gm-rp-paystatus"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending / Unpaid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Customer Search (name / phone / order#)</Label>
            <Input value={filters.query}
              onChange={e => setFilters(f => ({ ...f, query: e.target.value }))}
              data-testid="gm-rp-query" />
          </div>
        </div>
        <div className="flex flex-wrap gap-4 pt-1">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={filters.bulk_only}
              onCheckedChange={(v) => setFilters(f => ({ ...f, bulk_only: !!v }))}
              data-testid="gm-rp-bulk" />
            Bulk Orders Only ({">"} 100 pcs)
          </label>
          {isSA && (
            <label className="flex items-center gap-2 text-xs cursor-pointer"
                     data-testid="gm-rp-voided-wrap">
              <Checkbox checked={filters.include_voided}
                onCheckedChange={(v) => setFilters(f => ({ ...f, include_voided: !!v }))}
                data-testid="gm-rp-voided" />
              Include Voided / Deleted Orders
            </label>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <SummaryCard label="Total Orders" value={s.total_orders} />
          <SummaryCard label="Total Modaks" value={s.total_modaks} />
          <SummaryCard label="Confirmed Orders" value={s.confirmed_orders} tone="success" />
          <SummaryCard label="Payment Pending" value={s.payment_pending} tone="warn" />
          <SummaryCard label="Taxable Value" value={fmtInr(s.taxable_total)} />
          <SummaryCard label="GST @ 5%" value={fmtInr(s.gst_total)} />
          <SummaryCard label="Final (incl. GST)" value={fmtInr(s.final_total)} tone="success" />
          <SummaryCard label="Amount Received" value={fmtInr(s.amount_received)} />
          <SummaryCard label="Outstanding" value={fmtInr(s.outstanding_total)}
            tone={s.outstanding_total > 0 ? "danger" : undefined} />
          <SummaryCard label="Delivered / Picked Up" value={s.delivered_picked_up} />
          <SummaryCard label="Pending Pickup" value={s.pending_pickup} />
        </div>
      )}

      {/* Table */}
      <div className="border rounded overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order No.</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Centre</TableHead>
              <TableHead>Order Date</TableHead>
              <TableHead>Pickup</TableHead>
              <TableHead>Packages</TableHead>
              <TableHead className="text-right">Pcs</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Disc</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">GST 5%</TableHead>
              <TableHead className="text-right">Final (incl. GST)</TableHead>
              <TableHead className="text-right">Received</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead>Pay Status</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={19} className="text-center py-6">
                <Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && data.rows.length === 0 && (
              <TableRow><TableCell colSpan={19} className="text-center py-6 text-muted-foreground">
                No orders match these filters</TableCell></TableRow>
            )}
            {data.rows.map((r) => (
              <TableRow key={r.order_number} className={r.status === "VOIDED" ? "opacity-60 line-through" : ""}>
                <TableCell className="font-mono text-[11px]">{r.order_number}</TableCell>
                <TableCell>{r.customer_name}</TableCell>
                <TableCell className="text-xs">{r.phone}</TableCell>
                <TableCell>{r.center}</TableCell>
                <TableCell className="text-xs">{r.created_at}</TableCell>
                <TableCell className="text-xs">
                  <div>{r.pickup_date}</div>
                  <div className="text-muted-foreground">{r.pickup_time || "—"}</div>
                </TableCell>
                <TableCell className="text-xs">{r.package_details}</TableCell>
                <TableCell className="text-right font-semibold">{r.total_pieces}</TableCell>
                <TableCell className="text-right">{fmtInr(r.gross_amount)}</TableCell>
                <TableCell className="text-right">{fmtInr(r.bulk_discount + r.manual_discount)}</TableCell>
                <TableCell className="text-right">{fmtInr(r.taxable_amount)}</TableCell>
                <TableCell className="text-right">{fmtInr(r.gst_amount)}</TableCell>
                <TableCell className="text-right font-semibold">{fmtInr(r.final_amount)}</TableCell>
                <TableCell className="text-right">{fmtInr(r.amount_paid)}</TableCell>
                <TableCell className={`text-right ${r.outstanding > 0 ? "text-red-700 font-semibold" : ""}`}>
                  {fmtInr(r.outstanding)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    r.payment_status === "PAID" ? "bg-emerald-50 text-emerald-700"
                      : r.payment_status === "PARTIAL" ? "bg-amber-50 text-amber-700"
                      : "bg-red-50 text-red-700"
                  }>{r.payment_status}</Badge>
                </TableCell>
                <TableCell className="text-xs">{r.receipt_number || "—"}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLOR[r.status] || ""}>{r.status.replace(/_/g, " ")}</Badge>
                </TableCell>
                <TableCell className="text-xs">{r.created_by || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ProductionReportPreview({ token, onBack }) {
  const [centers, setCenters] = useState([]);
  const [filters, setFilters] = useState({
    center: "all",
    pickup_date: "",
    pickup_date_from: "",
    pickup_date_to: "",
    pickup_time: "",
    include_pending: true,
  });
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/centers`).then((r) => setCenters(r.data.centers || r.data || []));
  }, []);

  const payload = useMemo(() => {
    const p = { token };
    if (filters.center && filters.center !== "all") p.center = filters.center;
    if (filters.pickup_date) p.pickup_date = filters.pickup_date;
    if (filters.pickup_date_from) p.pickup_date_from = filters.pickup_date_from;
    if (filters.pickup_date_to) p.pickup_date_to = filters.pickup_date_to;
    if (filters.pickup_time) p.pickup_time = filters.pickup_time;
    p.include_pending = filters.include_pending;
    return p;
  }, [filters, token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${GM}/reports/production-preview`, payload);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load preview");
    } finally { setLoading(false); }
  }, [payload]);

  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    setDownloading(true);
    try {
      const endpoint = fmt === "pdf" ? "export/production-pdf" : "export/production";
      const mime = fmt === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const r = await axios.post(`${GM}/${endpoint}`, payload, { responseType: "blob" });
      const ext = fmt === "pdf" ? "pdf" : "xlsx";
      downloadBlob(r.data, `modak_production_${TODAY()}.${ext}`, mime);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally { setDownloading(false); }
  };

  const s = data.summary;
  return (
    <div className="space-y-3" data-testid="gm-production-report-preview">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="gm-report-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reports
          </Button>
          <div className="font-semibold text-[#8B0000]">Preview — Production Requirement</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="gm-print">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button size="sm" variant="outline" disabled={downloading}
            onClick={() => download("xlsx")}
            data-testid="gm-download-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Download Excel
          </Button>
          <Button size="sm" variant="outline" disabled={downloading}
            onClick={() => download("pdf")}
            data-testid="gm-download-pdf">
            <FileText className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border rounded p-3 bg-slate-50/60 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <div>
            <Label className="text-xs">Centre</Label>
            <Select value={filters.center} onValueChange={(v) => setFilters(f => ({ ...f, center: v }))}>
              <SelectTrigger data-testid="gm-pp-center"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Centres</SelectItem>
                {centers.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Pickup Date (exact)</Label>
            <Input type="date" value={filters.pickup_date}
              onChange={e => setFilters(f => ({ ...f, pickup_date: e.target.value }))}
              data-testid="gm-pp-pickup" />
          </div>
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={filters.pickup_date_from}
              onChange={e => setFilters(f => ({ ...f, pickup_date_from: e.target.value }))}
              data-testid="gm-pp-from" />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={filters.pickup_date_to}
              onChange={e => setFilters(f => ({ ...f, pickup_date_to: e.target.value }))}
              data-testid="gm-pp-to" />
          </div>
          <div>
            <Label className="text-xs">Pickup Time Slot (contains)</Label>
            <Input placeholder="e.g. 10:00 or AM"
              value={filters.pickup_time}
              onChange={e => setFilters(f => ({ ...f, pickup_time: e.target.value }))}
              data-testid="gm-pp-slot" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer pt-1"
                data-testid="gm-pp-include-pending-wrap">
          <Checkbox checked={filters.include_pending}
            onCheckedChange={(v) => setFilters(f => ({ ...f, include_pending: !!v }))}
            data-testid="gm-pp-include-pending" />
          Also show payment-pending potential (separate column — not added to confirmed production)
        </label>
      </div>

      {/* Summary */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <SummaryCard label="Confirmed Orders" value={s.total_confirmed_orders} tone="success" />
          <SummaryCard label="Confirmed Modaks" value={s.total_confirmed_modaks} tone="success" />
          <SummaryCard label="4-pc Packs" value={s.P4_packs} />
          <SummaryCard label="11-pc Packs" value={s.P11_packs} />
          <SummaryCard label="21-pc Packs" value={s.P21_packs} />
          <SummaryCard label="Bulk Pieces" value={s.bulk_pieces} />
          <SummaryCard label="Payment-Pending Potential" value={s.payment_pending_pieces} tone="warn" />
        </div>
      )}

      {/* Table */}
      <div className="border rounded overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Centre</TableHead>
              <TableHead>Pickup Date</TableHead>
              <TableHead>Pickup Time</TableHead>
              <TableHead className="text-right">4-pc Packs</TableHead>
              <TableHead className="text-right">11-pc Packs</TableHead>
              <TableHead className="text-right">21-pc Packs</TableHead>
              <TableHead className="text-right">Bulk Pieces</TableHead>
              <TableHead className="text-right">Confirmed Modaks</TableHead>
              {filters.include_pending && (
                <TableHead className="text-right text-amber-700">Pay-Pending Pcs</TableHead>
              )}
              {filters.include_pending && (
                <TableHead className="text-right">Total Potential</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={10} className="text-center py-6">
                <Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && data.rows.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center py-6 text-muted-foreground">
                No production data for these filters</TableCell></TableRow>
            )}
            {data.rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-semibold">{r.center}</TableCell>
                <TableCell>{r.pickup_date}</TableCell>
                <TableCell>{r.pickup_time || "—"}</TableCell>
                <TableCell className="text-right">{r.confirmed_P4}</TableCell>
                <TableCell className="text-right">{r.confirmed_P11}</TableCell>
                <TableCell className="text-right">{r.confirmed_P21}</TableCell>
                <TableCell className="text-right">{r.confirmed_bulk}</TableCell>
                <TableCell className="text-right font-bold text-emerald-800">{r.confirmed_pieces}</TableCell>
                {filters.include_pending && (
                  <TableCell className="text-right text-amber-700">{r.pending_pieces}</TableCell>
                )}
                {filters.include_pending && (
                  <TableCell className="text-right font-semibold">{r.total_potential}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Confirmed production requirement uses <strong>CONFIRMED + PAID</strong> orders only.
        Payment-pending quantities are shown separately and never mixed into confirmed totals.
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Delete / Void order dialog (SA + Admin only)
// ────────────────────────────────────────────────────────────────────
const DELETION_CATEGORIES = [
  { value: "DUPLICATE", label: "Duplicate Entry" },
  { value: "ORDER_CANCELLED", label: "Order Cancelled" },
  { value: "PAYMENT_REFUNDED", label: "Payment Refunded" },
  { value: "DATA_CORRECTION", label: "Data Correction" },
  { value: "OTHER", label: "Other" },
];

const SOFT_PROTECTED = new Set([
  "CONFIRMED", "LATE_PAYMENT_CONFIRMED",
  "IN_PRODUCTION", "READY", "PICKED_UP", "DELIVERED",
]);

function DeleteOrderDialog({ token, order, onClose, onDone }) {
  const hasPayment = Number(order.amount_paid || 0) > 0;
  const willSoftDelete = hasPayment || SOFT_PROTECTED.has(order.status);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [category, setCategory] = useState("");
  const [refund, setRefund] = useState({ amount: "", date: "", reference: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  const isRefund = category === "PAYMENT_REFUNDED";
  const canSubmit = reason.trim().length >= 3 && confirmed
    && (!willSoftDelete || !!category);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        token,
        order_number: order.order_number,
        reason: reason.trim(),
        confirmed: true,
        deletion_category: category || null,
      };
      if (isRefund && refund.amount) {
        payload.refund_amount = Number(refund.amount);
        payload.refund_date = refund.date || null;
        payload.refund_reference = refund.reference || null;
        payload.refund_remarks = refund.remarks || null;
      }
      const r = await axios.post(`${GM}/orders/delete`, payload);
      toast.success(r.data.action === "voided"
        ? `Order VOIDED — kept in audit history`
        : `Order deleted`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const t = order.totals || {};
  const finalAmt = t.final_payable ?? t.final_amount ?? 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto"
                       data-testid="gm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Delete Modak Order?
          </DialogTitle>
          <DialogDescription>
            {willSoftDelete
              ? "This order is confirmed/paid and will be VOIDED (not permanently erased). It will be removed from active operations & production reports but preserved for audit."
              : "This order is unpaid and can be permanently deleted. A full audit record will still be kept."}
          </DialogDescription>
        </DialogHeader>

        {/* Order snapshot */}
        <div className="border rounded p-3 bg-red-50/40 text-sm space-y-1">
          <div><strong>Order No.:</strong> <span className="font-mono">{order.order_number}</span></div>
          <div><strong>Customer:</strong> {order.customer_name}</div>
          <div><strong>Centre:</strong> {order.center}</div>
          <div><strong>Pickup:</strong> {order.pickup_date} {order.pickup_time || ""}</div>
          <div><strong>Pieces:</strong> {t.total_pieces || 0}</div>
          <div><strong>Amount:</strong> {fmtInr(finalAmt)}</div>
          <div>
            <strong>Status:</strong>{" "}
            <Badge className={STATUS_COLOR[order.status] || ""}>{order.status.replace(/_/g, " ")}</Badge>
          </div>
          <div><strong>Payment:</strong>{" "}
            {hasPayment
              ? <span className="text-emerald-700 font-semibold">PAID {fmtInr(order.amount_paid || 0)}</span>
              : <span className="text-red-700">UNPAID</span>}
          </div>
        </div>

        {/* Payment protection warning */}
        {hasPayment && (
          <Alert variant="destructive" data-testid="gm-delete-payment-warning">
            <AlertTriangle className="w-4 h-4" />
            <AlertTitle>PAYMENT EXISTS FOR THIS ORDER</AlertTitle>
            <AlertDescription className="text-xs space-y-1">
              <div>Amount Received: <strong>{fmtInr(order.amount_paid || 0)}</strong></div>
              <div>Payment Ref: {order.upi_txn_ref || "—"}</div>
              <div>Receipt / Bill: {order.receipt_number || "—"}</div>
              <div className="pt-1">This payment will NOT be silently erased. Choose a deletion category below and, if refunded, record refund details.</div>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label>Reason for deletion *</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. duplicate booking — same customer created twice within 5 minutes"
            data-testid="gm-delete-reason" />
        </div>

        {willSoftDelete && (
          <div>
            <Label>Deletion Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="gm-delete-category"><SelectValue placeholder="Choose reason category" /></SelectTrigger>
              <SelectContent>
                {DELETION_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isRefund && (
          <div className="border rounded p-2 bg-amber-50/60 space-y-2">
            <div className="text-xs font-semibold uppercase text-amber-800">Refund Details</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Refund Amount</Label>
                <Input type="number" value={refund.amount}
                  onChange={e => setRefund(r => ({ ...r, amount: e.target.value }))}
                  data-testid="gm-refund-amount" />
              </div>
              <div>
                <Label className="text-xs">Refund Date</Label>
                <Input type="date" value={refund.date}
                  onChange={e => setRefund(r => ({ ...r, date: e.target.value }))}
                  data-testid="gm-refund-date" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Refund Reference</Label>
                <Input value={refund.reference}
                  onChange={e => setRefund(r => ({ ...r, reference: e.target.value }))}
                  data-testid="gm-refund-ref" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Remarks</Label>
                <Textarea value={refund.remarks}
                  onChange={e => setRefund(r => ({ ...r, remarks: e.target.value }))}
                  data-testid="gm-refund-remarks" />
              </div>
            </div>
          </div>
        )}

        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)}
            data-testid="gm-delete-confirm" />
          <span>I confirm this order was created incorrectly / is a duplicate and should be removed.</span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
            data-testid="gm-delete-cancel">Cancel</Button>
          <Button variant="destructive" disabled={!canSubmit || busy} onClick={submit}
            data-testid="gm-delete-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {willSoftDelete ? "Void Order" : "Delete Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Campaign Settings tab (SA only)
// ────────────────────────────────────────────────────────────────────
function CampaignSettingsTab({ token, campaign, onSaved }) {
  const [c, setC] = useState({ ...campaign,
    default_pickup_dates: (campaign.default_pickup_dates || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...c, token,
        default_pickup_dates: c.default_pickup_dates.split(",").map((s) => s.trim()).filter(Boolean) };
      const r = await axios.post(`${GM}/campaign/save`, payload);
      toast.success("Campaign settings saved");
      onSaved(r.data.settings);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const setPkg = (idx, key, v) => setC((s) => ({
    ...s,
    packages: s.packages.map((p, i) => i === idx ? { ...p, [key]: (key === "price" || key === "pieces") ? Number(v) : v } : p),
  }));
  return (
    <div className="space-y-3" data-testid="gm-settings-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <Label>Campaign Year</Label>
          <Input type="number" value={c.campaign_year} onChange={(e) => setC((s) => ({ ...s, campaign_year: Number(e.target.value) }))} data-testid="gm-set-year" />
        </div>
        <div>
          <Label>Booking Start</Label>
          <Input type="date" value={c.booking_start_date} onChange={(e) => setC((s) => ({ ...s, booking_start_date: e.target.value }))} data-testid="gm-set-start" />
        </div>
        <div>
          <Label>Booking End</Label>
          <Input type="date" value={c.booking_end_date} onChange={(e) => setC((s) => ({ ...s, booking_end_date: e.target.value }))} data-testid="gm-set-end" />
        </div>
        <div>
          <Label>Default Pickup Dates (comma sep)</Label>
          <Input value={c.default_pickup_dates} onChange={(e) => setC((s) => ({ ...s, default_pickup_dates: e.target.value }))} data-testid="gm-set-pickup" />
        </div>
        <div>
          <Label>Payment Lead Days</Label>
          <Input type="number" value={c.payment_lead_days} onChange={(e) => setC((s) => ({ ...s, payment_lead_days: Number(e.target.value) }))} data-testid="gm-set-lead" />
        </div>
        <div>
          <Label>Bulk Discount Threshold (pieces)</Label>
          <Input type="number" value={c.bulk_discount_threshold_pieces} onChange={(e) => setC((s) => ({ ...s, bulk_discount_threshold_pieces: Number(e.target.value) }))} data-testid="gm-set-threshold" />
        </div>
        <div>
          <Label>Bulk Discount %</Label>
          <Input type="number" value={c.bulk_discount_percent} onChange={(e) => setC((s) => ({ ...s, bulk_discount_percent: Number(e.target.value) }))} data-testid="gm-set-pct" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={c.active} onChange={(e) => setC((s) => ({ ...s, active: e.target.checked }))} data-testid="gm-set-active" />
            Campaign Active
          </label>
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold mb-1">Packages</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Pieces/Box</TableHead>
              <TableHead>Price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {c.packages.map((p, i) => (
              <TableRow key={i}>
                <TableCell><Input value={p.code} onChange={(e) => setPkg(i, "code", e.target.value.toUpperCase())} /></TableCell>
                <TableCell><Input value={p.label} onChange={(e) => setPkg(i, "label", e.target.value)} /></TableCell>
                <TableCell><Input type="number" value={p.pieces} onChange={(e) => setPkg(i, "pieces", e.target.value)} /></TableCell>
                <TableCell><Input type="number" value={p.price} onChange={(e) => setPkg(i, "price", e.target.value)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Button onClick={save} disabled={busy} className="bg-[#8B0000] hover:bg-[#6b0000]" data-testid="gm-set-save">
        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save Campaign Settings
      </Button>

      <div className="border-t pt-4 mt-4">
        <CentrePaymentConfigSection token={token} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Centre Payment Configuration (SA-only, inside Campaign Settings tab)
// ────────────────────────────────────────────────────────────────────
function CentrePaymentConfigSection({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ center: "", upi_id: "", payee_name: "", qr_file: "", active: true });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${GM}/centre-payments/list`, { token });
      setRows(r.data.rows || []);
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    try {
      await axios.post(`${GM}/centre-payments/save`, { token, ...row });
      toast.success(`Saved ${row.center}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (center) => {
    if (!window.confirm(`Delete payment config for ${center}?`)) return;
    try {
      await axios.post(`${GM}/centre-payments/delete`, { token, center });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="gm-centre-payments-section">
      <div>
        <div className="text-sm font-semibold text-[#8B0000]">Centre Payment Configuration</div>
        <div className="text-xs text-muted-foreground">
          Each centre must have its own UPI ID + QR PDF. Orders will refuse to save until a centre has active payment config.
          The QR file must exist under <code className="bg-muted px-1 rounded">/app/backend/qr_assets/</code>.
        </div>
      </div>
      <div className="border rounded overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Centre</TableHead>
              <TableHead>UPI ID</TableHead>
              <TableHead>Payee Name</TableHead>
              <TableHead>QR File</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>QR Preview</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={7} className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>)}
            {rows.map((r, i) => (
              <CentrePaymentRow key={i} row={r} onSave={save} onDelete={() => del(r.center)} />
            ))}
            <TableRow className="bg-amber-50/30">
              <TableCell><Input value={draft.center} placeholder="PB-XX"
                onChange={(e) => setDraft((s) => ({ ...s, center: e.target.value.toUpperCase() }))}
                data-testid="gm-cp-new-center" /></TableCell>
              <TableCell><Input value={draft.upi_id} placeholder="ex: Q123@ybl"
                onChange={(e) => setDraft((s) => ({ ...s, upi_id: e.target.value }))}
                data-testid="gm-cp-new-upi" /></TableCell>
              <TableCell><Input value={draft.payee_name}
                onChange={(e) => setDraft((s) => ({ ...s, payee_name: e.target.value }))}
                data-testid="gm-cp-new-payee" /></TableCell>
              <TableCell><Input value={draft.qr_file} placeholder="PB-XX-QXXX.pdf"
                onChange={(e) => setDraft((s) => ({ ...s, qr_file: e.target.value }))}
                data-testid="gm-cp-new-qr" /></TableCell>
              <TableCell><input type="checkbox" checked={draft.active}
                onChange={(e) => setDraft((s) => ({ ...s, active: e.target.checked }))} /></TableCell>
              <TableCell />
              <TableCell>
                <Button size="sm" onClick={() => { save(draft); setDraft({ center: "", upi_id: "", payee_name: "", qr_file: "", active: true }); }}
                  disabled={!draft.center || !draft.upi_id} data-testid="gm-cp-new-save">
                  Add
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CentrePaymentRow({ row, onSave, onDelete }) {
  const [r, setR] = useState(row);
  const dirty = JSON.stringify(r) !== JSON.stringify(row);
  return (
    <TableRow data-testid={`gm-cp-row-${row.center}`}>
      <TableCell className="font-mono text-xs">{r.center}</TableCell>
      <TableCell><Input value={r.upi_id || ""} onChange={(e) => setR({ ...r, upi_id: e.target.value })} /></TableCell>
      <TableCell><Input value={r.payee_name || ""} onChange={(e) => setR({ ...r, payee_name: e.target.value })} /></TableCell>
      <TableCell><Input value={r.qr_file || ""} onChange={(e) => setR({ ...r, qr_file: e.target.value })} /></TableCell>
      <TableCell><input type="checkbox" checked={!!r.active} onChange={(e) => setR({ ...r, active: e.target.checked })} /></TableCell>
      <TableCell>
        <a className="underline text-blue-700 text-xs"
          href={`${API}/api/ganesh-modak/qr/${r.center}`} target="_blank" rel="noreferrer">Open QR</a>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button size="sm" onClick={() => onSave(r)} disabled={!dirty}
            data-testid={`gm-cp-save-${row.center}`}>Save</Button>
          <Button size="sm" variant="destructive" onClick={onDelete}
            data-testid={`gm-cp-delete-${row.center}`}>Delete</Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
